import { useMemo } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import type { CustomViewTodoItemDTO } from "../../managers/customView/types/customViewData.api";
import type {
    CustomView,
    CustomViewInput,
    CustomViewType,
    ViewFilter,
} from "../../managers/customView/types/customViews";
import type {
    AttachmentMetadata,
    LinkedEventDetails,
    LinkObject,
} from "../../managers/mail/types/mail";

export type CustomViews = {
    getCustomViews: () => Promise<CustomView[]>;
    getView: (customViewId: string) => Promise<CustomView>;
    createView: (
        customView: CustomViewInput,
        customViewType?: CustomViewType,
    ) => Promise<CustomView>;
    editView: (
        customViewId: string,
        updatedCustomView: CustomView,
    ) => Promise<void>;
    deleteView: (customViewId: string) => Promise<void>;
    setFavoriteViews: (orderedViewsIds: string[]) => Promise<void>;
    generateViewFilter: (userPrompt: string) => Promise<ViewFilter>;
    getViewTodos: (customViewId: string) => Promise<CustomViewTodoItemDTO[]>;
    getCustomViewAttachments: (
        customViewId: string,
    ) => Promise<AttachmentMetadata[]>;
    getCustomViewLinks: (customViewId: string) => Promise<LinkObject[]>;
    getCustomViewEvents: (
        customViewId: string,
    ) => Promise<LinkedEventDetails[]>;
};

export function useCustomViews(): CustomViews {
    const { customViewManager } = useSdk();

    return useMemo<CustomViews>(
        () => ({
            getCustomViews: () => customViewManager.getCustomViews(),
            getView: (...args) => customViewManager.getView(...args),
            createView: (...args) => customViewManager.createView(...args),
            editView: (...args) => customViewManager.editView(...args),
            deleteView: (...args) => customViewManager.deleteView(...args),
            setFavoriteViews: (...args) =>
                customViewManager.setFavoriteViews(...args),
            generateViewFilter: (...args) =>
                customViewManager.generateViewFilter(...args),
            getViewTodos: (...args) => customViewManager.getViewTodos(...args),
            getCustomViewAttachments: (...args) =>
                customViewManager.getCustomViewAttachments(...args),
            getCustomViewLinks: (...args) =>
                customViewManager.getCustomViewLinks(...args),
            getCustomViewEvents: (...args) =>
                customViewManager.getCustomViewEvents(...args),
        }),
        [customViewManager],
    );
}
